import React, { useState, useEffect, useRef } from 'react';
import { Stage, Layer, Rect, Line, Group, Text } from 'react-konva';
import { Box, Button, Typography, makeStyles } from '@material-ui/core';
import { TableGroup }         from '../../common/tablePlacement/konva/TableGroup';
import { ReservationTooltip } from './ReservationTooltip';
import { Get }                from '../../../actions';

import {
  CANVAS_WIDTH,
  CANVAS_HEIGHT,
  getDim,
  toNorm,
} from '../../common/tablePlacement/canvasConstants';
import { getTranslation } from '../../../heplers/translationHelper';

const useStyles = makeStyles(() => ({
  root: {
    background: '#fff',
    borderRadius: '12px',
    border: '1px solid #e8edf2',
    boxShadow: '0 2px 12px rgba(0,68,103,0.08)',
    overflow: 'hidden',
  },
  areaBar: {
    display: 'flex',
    gap: '8px',
    padding: '10px 16px',
    borderBottom: '1px solid #e8edf2',
    flexWrap: 'wrap',
    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
    alignItems: 'center',
  },
  areaBarLabel: {
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.7px',
    textTransform: 'uppercase',
    color: '#8a9bb0',
    marginRight: '4px',
    flexShrink: 0,
  },
  areaBtn: {
    borderRadius: '20px !important',
    textTransform: 'none !important',
    fontSize: '13px !important',
    fontWeight: '600 !important',
    padding: '3px 14px !important',
    minHeight: 'unset !important',
  },
  canvasOuter: {
    width: '100%',
    overflow: 'hidden',
    position: 'relative',
    background: '#fafbfc',
  },
  emptyMsg: {
    padding: '40px 16px',
    textAlign: 'center',
    color: '#8a9bb0',
  },
}));

const SKELETON_POSITIONS = [
  { x: 80,  y: 100, w: 130, h: 60 },
  { x: 280, y: 100, w: 70,  h: 70 },
  { x: 430, y: 100, w: 90,  h: 60 },
  { x: 80,  y: 240, w: 60,  h: 130 },
  { x: 220, y: 240, w: 130, h: 60 },
];

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

// keep every table inside the room, so one bad position cannot push it off the plan
const normalize = (t) => {
  const dim = getDim(t.shape, t.persons);
  const hw  = dim.w / 2 / CANVAS_WIDTH;
  const hh  = dim.h / 2 / CANVAS_HEIGHT;
  return {
    ...t,
    positionX: clamp(toNorm(t.positionX, CANVAS_WIDTH)  || 0, hw, 1 - hw),
    positionY: clamp(toNorm(t.positionY, CANVAS_HEIGHT) || 0, hh, 1 - hh),
  };
};

const LINK_COLORS = ['#1565C0', '#E65100', '#2E7D32', '#6A1B9A', '#00838F', '#AD1457', '#F57F17', '#37474F'];

const LABEL_FONT  = 13;  // on-screen px, kept constant whatever the plan scale
const LABEL_PAD_X = 8;
const LABEL_PAD_Y = 4;
const LABEL_MAX_CHARS = 24;

const reservedIds = (res) =>
  res.tableReserved
    ? res.tableReserved.split('|').map((item) => item.split(',')[0].trim()).filter(Boolean)
    : [];

const center = (t) => ({ x: t.positionX * CANVAS_WIDTH, y: t.positionY * CANVAS_HEIGHT });

// distance from the table center to its edge along the unit direction (ux, uy)
const edgeDistance = (t, ux, uy) => {
  const dim = getDim(t.shape, t.persons);
  if (t.shape === 'circle') return dim.w / 2;
  const tx = Math.abs(ux) > 0.001 ? dim.w / 2 / Math.abs(ux) : Infinity;
  const ty = Math.abs(uy) > 0.001 ? dim.h / 2 / Math.abs(uy) : Infinity;
  return Math.min(tx, ty);
};

// one entry per reservation: the lines between its tables (edge to edge) and
// where to show the customer name
const computeOverlays = (reservations, enrichedTables) => {
  const overlays = [];
  reservations.forEach((res, idx) => {
    const tables = reservedIds(res)
      .map((id) => enrichedTables.find((t) => String(t.id) === id))
      .filter(Boolean);
    if (tables.length === 0) return;

    const color = LINK_COLORS[idx % LINK_COLORS.length];
    const name  = (res.title ?? '').trim();
    const label = name.length > LABEL_MAX_CHARS ? `${name.slice(0, LABEL_MAX_CHARS - 1)}…` : name;

    if (tables.length === 1) {
      const t   = tables[0];
      const c   = center(t);
      const dim = getDim(t.shape, t.persons);
      overlays.push({ key: res.id ?? idx, color, label, lines: [], anchor: { ...c, below: dim.h / 2 } });
      return;
    }

    const lines = [];
    let longest = null;
    for (let i = 0; i < tables.length - 1; i++) {
      const a = center(tables[i]);
      const b = center(tables[i + 1]);
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const len = Math.sqrt(dx * dx + dy * dy);
      const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      if (!longest || len > longest.len) longest = { len, mid };
      if (len === 0) continue;
      const ux = dx / len;
      const uy = dy / len;
      const da = edgeDistance(tables[i],     ux,  uy);
      const db = edgeDistance(tables[i + 1], -ux, -uy);
      // tables touching or overlapping: nothing to draw between them
      if (da + db >= len) continue;
      lines.push([a.x + ux * da, a.y + uy * da, b.x - ux * db, b.y - uy * db]);
    }
    overlays.push({ key: res.id ?? idx, color, label, lines, anchor: { ...longest.mid, below: null } });
  });
  return overlays;
};

// customer name in a pill; `inv` converts on-screen px to canvas units
const NamePill = ({ anchor, label, color, inv }) => {
  if (!label) return null;
  const font = LABEL_FONT * inv;
  const h    = font + LABEL_PAD_Y * 2 * inv;
  const w    = label.length * font * 0.6 + LABEL_PAD_X * 2 * inv;
  const gap  = 4 * inv;
  let x = anchor.x - w / 2;
  let y;
  if (anchor.below == null) {
    y = anchor.y - h / 2;
  } else {
    // under the table, or above it when there is no room at the bottom of the plan
    y = anchor.y + anchor.below + gap;
    if (y + h > CANVAS_HEIGHT) y = anchor.y - anchor.below - gap - h;
  }
  x = clamp(x, 0, Math.max(0, CANVAS_WIDTH - w));
  y = clamp(y, 0, Math.max(0, CANVAS_HEIGHT - h));
  return (
    <Group x={x} y={y} listening={false}>
      <Rect
        width={w}
        height={h}
        cornerRadius={h / 2}
        fill="#fff"
        stroke={color}
        strokeWidth={2 * inv}
        shadowColor="rgba(0,0,0,0.25)"
        shadowBlur={4 * inv}
        shadowOffsetY={1 * inv}
      />
      <Text
        width={w}
        height={h}
        text={label}
        fontSize={font}
        fontStyle="bold"
        fill={color}
        align="center"
        verticalAlign="middle"
        wrap="none"
      />
    </Group>
  );
};

export const SeatingPlanCanvas = ({
  reservations = [],
  queryParams,
  timeSlots = [],
  selectedTimeSlot,
  onTimeSlotClick,
  isLoadingTimeSlots = false,
  selectedDayTime = 0,
  slotCount,
}) => {
  const classes = useStyles();
  const [areas,        setAreas       ] = useState([]);
  const [selectedArea, setSelectedArea] = useState(null);
  const [areaTables,   setAreaTables  ] = useState([]);
  const [isLoading,    setIsLoading   ] = useState(false);
  const [hoveredTable, setHoveredTable] = useState(null);
  const [containerW,   setContainerW  ] = useState(0);
  const outerRef = useRef(null);

  // Track the available width so the plan can be zoomed to fill it
  useEffect(() => {
    const updateWidth = () => {
      if (outerRef.current) setContainerW(outerRef.current.offsetWidth);
    };
    updateWidth();
    const ro = new ResizeObserver(updateWidth);
    if (outerRef.current) ro.observe(outerRef.current);
    return () => ro.disconnect();
  }, []);

  const getStatus = (table) => {
    const isBooked = reservations.some((r) =>
      r.tableReserved?.split('|').some((item) => item.split(',')[0] === String(table.id))
    );
    if (isBooked)        return 'booked';
    if (table.available) return 'available';
    return 'blocked';
  };

  useEffect(() => {
    Get(
      {},
      'TableManagement/GetAllAreas',
      null,
      (res) => {
        setAreas(res.data);
        if (res.data.length > 0) setSelectedArea(res.data[0]);
      },
      (err) => console.error('Error loading areas:', err)
    );
  }, []);

  useEffect(() => {
    if (!selectedArea) return;
    setAreaTables([]);
    setIsLoading(true);
    Get(
      {
        areaId:     selectedArea.id,
        date:       queryParams.selectedDate,
        timeSlotId: selectedTimeSlot?.id,
        dayTime:    selectedDayTime,
      },
      'Reservation/GetAvailableTables',
      null,
      (res) => {
        setAreaTables(res.data.map(normalize));
        setIsLoading(false);
      },
      (err) => {
        console.error('Error loading tables:', err);
        setIsLoading(false);
      }
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTimeSlot, selectedArea, queryParams.selectedDate, selectedDayTime]);

  const enrichedTables = areaTables.map((t) => ({ ...t, status: getStatus(t) }));

  // show the whole plan as drawn in the settings, scaled down to fit the available width
  const scale  = containerW > 0 ? Math.min(1, containerW / CANVAS_WIDTH) : 0;
  const stageH = Math.ceil(CANVAS_HEIGHT * scale);
  const offset = { x: 0, y: 0 };
  const inv    = scale > 0 ? 1 / scale : 1;

  const overlays = isLoading ? [] : computeOverlays(reservations, enrichedTables);

  return (
    <Box className={classes.root}>

      {/* Area selector — horizontal tab bar */}
      <Box className={classes.areaBar}>
        <span className={classes.areaBarLabel}>{getTranslation("Area:")}</span>
        {areas.map((area) => (
          <Button
            key={area.id}
            className={classes.areaBtn}
            variant={selectedArea?.id === area.id ? 'contained' : 'outlined'}
            color={selectedArea?.id === area.id ? 'primary' : 'default'}
            size="small"
            onClick={() => setSelectedArea(area)}
          >
            {area.title}
          </Button>
        ))}
      </Box>

      {/* Canvas: zoomed on the tables, rendered at real size so it stays sharp */}
      <Box ref={outerRef} className={classes.canvasOuter}>
        {!isLoading && selectedArea && enrichedTables.length === 0 ? (
          <Typography className={classes.emptyMsg}>
            {getTranslation(
              'No tables in this area.',
              'Aucune table dans cette salle.',
              'Keine Tische in diesem Bereich.'
            )}
          </Typography>
        ) : scale > 0 && (
          <Stage
            width={containerW}
            height={stageH}
            scaleX={scale}
            scaleY={scale}
            x={offset.x}
            y={offset.y}
          >
            <Layer>
              {isLoading && SKELETON_POSITIONS.map((s, i) => (
                <Rect
                  key={i}
                  x={s.x} y={s.y} width={s.w} height={s.h}
                  fill="#e0e0e0" cornerRadius={4} opacity={0.7}
                />
              ))}
              {!isLoading && enrichedTables.map((table) => (
                <TableGroup
                  key={table.id}
                  table={table}
                  enableDrag={false}
                  onDragEnd={() => {}}
                  onHover={setHoveredTable}
                  onClick={null}
                  solid
                  labelFontSize={16}
                  showSeats
                />
              ))}
              {/* drawn above the tables: links between merged tables, then the customer names */}
              {overlays.map((o) => o.lines.map((points, i) => (
                <React.Fragment key={`link-${o.key}-${i}`}>
                  <Line
                    points={points}
                    stroke="#fff"
                    strokeWidth={7 * inv}
                    lineCap="round"
                    listening={false}
                  />
                  <Line
                    points={points}
                    stroke={o.color}
                    strokeWidth={4 * inv}
                    dash={[10 * inv, 6 * inv]}
                    lineCap="round"
                    listening={false}
                  />
                </React.Fragment>
              )))}
              {overlays.map((o) => (
                <NamePill
                  key={`name-${o.key}`}
                  anchor={o.anchor}
                  label={o.label}
                  color={o.color}
                  inv={inv}
                />
              ))}
            </Layer>
          </Stage>
        )}
      </Box>

      <ReservationTooltip
        table={hoveredTable}
        reservations={reservations}
        canvasRef={outerRef}
        scale={scale}
        offset={offset}
      />

    </Box>
  );
};
